"use client";

import { useState } from "react";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  AC_HORSE_POWER_MAX,
  AC_HORSE_POWER_MIN,
  AC_HORSE_POWER_PRESETS,
  type AcSpecFormState,
} from "@/lib/listing/acListingSpec";
import {
  APPLIANCE_INVERTER_TYPES,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import { cn } from "@/lib/utils";

interface AcSpecFieldsProps {
  value: AcSpecFormState;
  onChange: (next: AcSpecFormState) => void;
  className?: string;
}

function formatInverterLabel(value: ApplianceInverterType): string {
  return value === "inverter" ? "Inverter" : "Non-inverter";
}

function formatHpPreset(hp: number): string {
  return `${hp} HP`;
}

/**
 * Step 2 panel for air conditioner (canonical `air-conditioner`).
 * Horse power + inverter required; remote / installation kit optional.
 */
export default function AcSpecFields({
  value,
  onChange,
  className = "",
}: AcSpecFieldsProps) {
  const [extrasOpen, setExtrasOpen] = useState(
    value.acRemoteIncluded || value.acInstallationKitIncluded,
  );
  const [customHp, setCustomHp] = useState("");

  const applyCustomHp = () => {
    const parsed = Number.parseFloat(customHp);
    if (!Number.isFinite(parsed)) return;
    if (parsed < AC_HORSE_POWER_MIN || parsed > AC_HORSE_POWER_MAX) return;
    onChange({ ...value, horsePower: parsed });
    setCustomHp("");
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Horse power (HP) <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...AC_HORSE_POWER_PRESETS]}
          selected={
            typeof value.horsePower === "number" &&
            (AC_HORSE_POWER_PRESETS as readonly number[]).includes(
              value.horsePower,
            )
              ? value.horsePower
              : ""
          }
          onSelect={(hp: number) => onChange({ ...value, horsePower: hp })}
          formatLabel={formatHpPreset}
        />
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            type="number"
            inputMode="decimal"
            min={AC_HORSE_POWER_MIN}
            max={AC_HORSE_POWER_MAX}
            step={0.5}
            placeholder="Other HP"
            value={
              customHp !== ""
                ? customHp
                : typeof value.horsePower === "number" &&
                    !(AC_HORSE_POWER_PRESETS as readonly number[]).includes(
                      value.horsePower,
                    )
                  ? String(value.horsePower)
                  : ""
            }
            onChange={(e) => setCustomHp(e.target.value)}
            onBlur={applyCustomHp}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyCustomHp();
              }
            }}
            className="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            aria-label="Custom horse power"
          />
          <span className="text-xs text-text-light">
            Or type a value ({AC_HORSE_POWER_MIN}–{AC_HORSE_POWER_MAX})
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Inverter type <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...APPLIANCE_INVERTER_TYPES]}
          selected={value.applianceInverterType}
          onSelect={(applianceInverterType: ApplianceInverterType) =>
            onChange({ ...value, applianceInverterType })
          }
          formatLabel={formatInverterLabel}
        />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setExtrasOpen((o) => !o)}
          className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
        >
          {extrasOpen ? "Hide extras" : "Extras (optional)"}
        </button>
        {extrasOpen ? (
          <div className="space-y-3 rounded-md border border-border/60 p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={value.acRemoteIncluded}
                onChange={(e) =>
                  onChange({ ...value, acRemoteIncluded: e.target.checked })
                }
              />
              Remote included
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={value.acInstallationKitIncluded}
                onChange={(e) =>
                  onChange({
                    ...value,
                    acInstallationKitIncluded: e.target.checked,
                  })
                }
              />
              Installation kit included
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );
}
